/**
 * Audit API Service
 * Provides methods for user-level audit logging and action history retrieval
 */

import { post, get } from './api'
import { ENDPOINTS } from './endpoints'

// ============================================================================
// Types
// ============================================================================

export type AuditResource =
  | 'WORK_ORDER' | 'ALERT' | 'WORKFLOW_STEP' | 'CHECKLIST'
  | 'ASSET' | 'MODEL' | 'PREDICTION' | 'REPORT' | 'SETTINGS'

export type AuditAction =
  | 'CREATE' | 'UPDATE' | 'DELETE' | 'ACCESS' | 'EXPORT'
  | 'APPROVE' | 'REJECT' | 'ACTIVATE' | 'DEACTIVATE'

export interface AuditLogParams {
  resource: AuditResource | string
  action: AuditAction | string
  resource_id: string
  details?: Record<string, unknown>
}

export interface AuditEntry {
  id: string
  action: string
  resource_type: string
  resource_id: string
  resource_name?: string
  actor_id: string
  actor_name: string
  actor_type: string
  details: Record<string, unknown>
  severity?: string
  success?: boolean
  timestamp: string
}

// ============================================================================
// Audit API
// ============================================================================

export const auditApi = {
  /** Log a user action for audit trail */
  async logAction(params: AuditLogParams): Promise<{ status: string }> {
    try {
      return await post<{ status: string }>(ENDPOINTS.AUDIT_USER_LOG, params)
    } catch {
      // Audit logging should never block the UI — fail silently
      console.warn('Audit log failed:', params.resource, params.action, params.resource_id)
      return { status: 'failed' }
    }
  },

  /** Get action history for the current user's organization */
  async getActionHistory(params?: {
    resource_type?: string
    resource_id?: string
    limit?: number
    offset?: number
  }): Promise<{ entries: AuditEntry[]; total: number }> {
    return get(ENDPOINTS.AUDIT_USER_HISTORY, { params })
  },
}
